import { useEffect, useRef, useState } from 'react';

import { Button, Card } from '@/design-system';
import type { ReportInvoiceChoice, ReportRecipient } from '@/entities/report';

import { reportCopy } from '../copy';
import { useInvoiceChoice, type InvoiceEnclosureSetup } from '../useInvoiceChoice';
import { ClientLanguageField } from './ClientLanguageField';
import { InvoiceEnclosure } from './InvoiceEnclosure';
import { RecipientList } from './RecipientList';

export interface ApprovalConfirmProps {
    recipients: readonly ReportRecipient[];
    busy: boolean;
    /**
     * Factuur meesturen: wat de API over de factuur van deze keuring zegt en het
     * standaard eigen bericht. Alleen voor wie facturen mag opstellen en uitgeven.
     */
    invoice?: InvoiceEnclosureSetup;
    /** De talen waarin de klant het rapport kan krijgen; één taal: geen keuze. */
    languages?: readonly string[];
    /**
     * Goedkeuren én versturen vanuit Smartkeur; met de factuur als die meegaat, en de
     * taal voor de klant (`undefined`: Nederlands).
     */
    onConfirm: (invoice: ReportInvoiceChoice | undefined, language?: string) => void;
    /** Alleen vaststellen: het rapport staat vast, kantoor verstuurt de link zelf. */
    onRelease: () => void;
    onCancel: () => void;
    /** Naar "Klant bewerken" in het dossier; alleen voor wie klanten mag wijzigen. */
    onEditClient?: () => void;
}

/**
 * Bevestiging van "Goedkeuren en versturen".
 *
 * Geen blokkerende modal - die bestaat niet in dit ontwerp - maar een blok dat
 * bovenaan het werkvlak verschijnt en onomwonden zegt wat er gebeurt: het
 * rapport gaat *nu* naar deze ontvangers en dat kan niet terug. Wie geen
 * e-mailadres heeft, krijgt niets; dat staat er ook, met de weg naar "Klant
 * bewerken" om het e-mailadres in te vullen.
 *
 * Kan de factuur mee, dan staat "Factuur meesturen" er standaard aan, met het
 * eigen bericht uit de instellingen: de klant krijgt dan rapport en factuur in één
 * mail. Wat er met de factuur gebeurt (gemaakt, definitief met een nummer) staat
 * erbij, want ook dat kan niet terug.
 *
 * Daarnaast de tweede weg: alleen vaststellen. Dan staat het rapport net zo
 * vast en bestaat de link, maar verstuurt kantoor hem zelf. Die knop blijft ook
 * werken zonder ontvanger met e-mailadres, want er gaat niets de deur uit.
 */
export function ApprovalConfirm({
    recipients,
    busy,
    invoice,
    languages = ['nl'],
    onConfirm,
    onRelease,
    onCancel,
    onEditClient,
}: ApprovalConfirmProps) {
    const confirmRef = useRef<HTMLButtonElement>(null);
    const [language, setLanguage] = useState('nl');
    const deliverable = recipients.filter((recipient) => recipient.email !== null);
    const skipped = recipients.length - deliverable.length;
    const enclosure = useInvoiceChoice(invoice, recipients);

    useEffect(() => {
        confirmRef.current?.focus();
    }, []);

    return (
        <Card
            variant="warn"
            padding="lg"
            role="group"
            aria-labelledby="rapport-bevestigen"
            className="mb-[14px]"
        >
            <h2
                id="rapport-bevestigen"
                className="m-0 mb-2 font-[family-name:var(--font-display)] text-[15px] font-semibold leading-[1.3] text-[var(--warn-ink)]"
            >
                {reportCopy.confirmTitle}
            </h2>
            <p className="m-0 text-[14px] leading-[1.5] text-[var(--warn-ink)]">
                {reportCopy.confirmBody}
            </p>

            <RecipientList recipients={recipients} tone="warn" />

            {deliverable.length > 0 ? (
                <ClientLanguageField
                    languages={languages}
                    value={language}
                    onChange={setLanguage}
                    disabled={busy}
                />
            ) : null}

            {invoice && deliverable.length > 0 ? (
                <InvoiceEnclosure {...enclosure.fields} disabled={busy} tone="warn" />
            ) : null}

            <p className="m-0 mt-4 text-[13px] leading-[1.5] text-[var(--warn-ink)]">
                {reportCopy.confirmOnlyReleaseHint}
            </p>

            <div className="mt-4 flex flex-wrap gap-2">
                <Button
                    ref={confirmRef}
                    variant="primary"
                    size="md"
                    disabled={busy || deliverable.length === 0}
                    onClick={() =>
                        onConfirm(enclosure.choice, language === 'nl' ? undefined : language)
                    }
                >
                    {reportCopy.confirmSend(deliverable.length)}
                </Button>
                <Button variant="outline" size="md" disabled={busy} onClick={onRelease}>
                    {reportCopy.confirmOnlyRelease}
                </Button>
                {onEditClient && (deliverable.length === 0 || skipped > 0) ? (
                    <Button variant="outline" size="md" disabled={busy} onClick={onEditClient}>
                        {reportCopy.editClient}
                    </Button>
                ) : null}
                <Button variant="outline" size="md" disabled={busy} onClick={onCancel}>
                    {reportCopy.confirmCancel}
                </Button>
            </div>
        </Card>
    );
}
